/**
 * @matcha/app-config — the ONE place the owner sets every go-live value.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 * Historically the paste-to-activate constants were scattered across three
 * build targets (mobile RN, marketing site, CDK infra). This module unifies
 * every PUBLIC / publishable value into a single typed object. Mobile,
 * marketing and infra all read from here; their local modules re-export from
 * this file so their public APIs (and their tests) are unchanged.
 *
 * Zero runtime dependencies + plain TS ⇒ it bundles in Metro (React Native),
 * Vite (marketing) and ts-node (CDK synth) alike. See `GO_LIVE.md` at the
 * repo root for the human checklist generated from these fields.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ENVIRONMENTS (sandbox | production)
 * ─────────────────────────────────────────────────────────────────────────
 * `appConfig` is resolved for the ACTIVE environment, selected by `APP_ENV`:
 *   - Mobile: the Xcode build config sets it (Debug → sandbox, Release →
 *     production) and babel inlines `process.env.APP_ENV` into the bundle.
 *   - CDK / marketing: `APP_ENV=… cdk deploy` / `APP_ENV=… vite build`.
 *   - Absent / anything else ⇒ `sandbox` — today's ONLY real environment (the
 *     current account + TestFlight builds). Untagged builds + deploys keep
 *     hitting the working sandbox stack unchanged. `production` is the
 *     scaffold you activate (`APP_ENV=production`) when you split.
 *
 * Only the values that genuinely differ per environment live in
 * `ENVIRONMENTS` below (bundle ids, RevenueCat key, Cognito pool + API URL,
 * APNs bundle, App Store id, the Sentry `environment` tag). Everything else is
 * shared `base` config. Isolation model (owner decision 2026-07):
 *   - HARD (separate project/account): AWS account, Neon DB, Cognito pool,
 *     RevenueCat app — each environment gets its own.
 *   - SOFT (one project, env tag): Sentry + PostHog stay a single project and
 *     are split by the `environment` tag / property, to preserve free-tier
 *     quota while staying filterable.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * PUBLIC vs SECRET
 * ─────────────────────────────────────────────────────────────────────────
 * Everything in this file is PUBLIC / safe to commit:
 *   - RevenueCat *public* SDK keys, PostHog *project* API key, Cognito
 *     Hosted-UI domain, App Store numeric id, bundle ids, legal + marketing
 *     URLs, company info, display prices, feature-flag defaults, and the
 *     public Route53 / APNs identifiers.
 * TRUE SECRETS are NEVER inlined here — the APNs `.p8` private key, social
 * OAuth client secrets, the CloudFront signing private key, the RevenueCat
 * webhook value, and the DB URL live in SSM SecureString or Lambda env. This
 * file records only their stable parameter NAMES (`infra.ssmParams`); setup
 * commands are documented in `GO_LIVE.md`.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * KEYLESS-SAFE CONTRACT
 * ─────────────────────────────────────────────────────────────────────────
 * Unset values are `''` / `null` on purpose. Empty ⇒ a graceful no-op with
 * UNCHANGED behaviour (the stub purchases impl, the no-op analytics impl,
 * hidden social-auth buttons, the store-root fallback link). Nothing is
 * "activated" until the owner pastes a real value here or completes the
 * documented secret setup/deployment step.
 */

/** Where a checked-in value is authored. Documentation only — surfaced in
 *  the generated GO_LIVE.md so each row says exactly where to set it. */
export type ConfigLocation = "public-commit" | "secret-ssm";

/** The two deployment environments. `production` is the App Store target;
 *  `sandbox` is the isolated pre-prod stack (own AWS account, Neon DB,
 *  Cognito pool, RevenueCat app). */
export type AppEnv = "sandbox" | "production";

/**
 * Resolve the active environment. Reads `APP_ENV` (build-time inlined on
 * mobile, process env on CDK/marketing).
 *
 * Default is `sandbox`: today there is exactly ONE real environment (the
 * current account + TestFlight builds), and it is the sandbox. Untagged
 * builds/deploys therefore keep hitting the working sandbox stack with no
 * change. `production` is the scaffold you activate when you split — at
 * that point Release builds set `APP_ENV=production` explicitly.
 */
// Module-local ambient so this zero-dep package typechecks without
// @types/node. It shadows the global `process` only inside this file, and
// crucially keeps the BARE `process.env.APP_ENV` member expression that
// babel's transform-inline-environment-variables rewrites to a literal at
// mobile bundle time (CDK/marketing read it at runtime in node).
declare const process: { env: Record<string, string | undefined> } | undefined;

export function currentAppEnv(): AppEnv {
  const raw =
    typeof process !== "undefined" ? process.env["APP_ENV"] : undefined;
  return raw === "production" ? "production" : "sandbox";
}

/** The env-specific slice of config. Everything here differs between
 *  `sandbox` and `production`; everything else is shared. */
interface EnvironmentConfig {
  bundleIds: { ios: string; android: string };
  /** RevenueCat public SDK keys — a separate RC app per environment. */
  revenueCat: { iosKey: string; androidKey: string };
  /** Cognito pool + backend API — a separate pool + stack per environment. */
  cognito: {
    userPoolId: string;
    userPoolClientId: string;
    backendApiUrl: string;
    oauthDomain: string;
    /** Shared header every request carries. See the exported member. */
    apiClientKey: string;
  };
  /** The ADMIN console's own Cognito pool + API. Deliberately a DIFFERENT
   *  pool from `cognito` above: the issuer URL differs, so a user-facing
   *  token can never authenticate against an admin route. Consumed by
   *  `apps/admin-web`. */
  admin: {
    userPoolId: string;
    userPoolClientId: string;
    apiUrl: string;
  };
  /** Numeric App Store id (separate ASC record per environment). */
  appStoreNumericId: string | null;
  /**
   * Whether that id resolves to a PUBLIC listing yet.
   *
   * An id exists from the moment the App Store Connect record is created;
   * a page exists only once a version has been released. Between the two,
   * `apps.apple.com/app/id…` is a 404, and every surface that links there
   * — the rate row, the share-the-app row, the force-upgrade CTA — sends
   * people to a dead page while looking perfectly healthy.
   *
   * Confirmed empirically rather than assumed: on 4 September 2026
   * `itunes.apple.com/lookup?id=6807339084` answered `resultCount: 0`
   * while the version sat in Prepare for Submission.
   *
   * FLIP THIS ON RELEASE DAY. It is the one line that turns those rows on.
   */
  appStoreListingIsLive: boolean;
  /** APNs bundle id — matches `bundleIds.ios`. */
  apnsBundleId: string;
  /** Sentry `environment` tag on the SHARED Sentry project. */
  sentryEnvironment: AppEnv;
  /** Deploy-time feature defaults, PER ENVIRONMENT. Production ships with
   *  social auth + push ON so a plain deploy can't accidentally disable
   *  them; sandbox stays OFF unless a deploy opts in via env. */
  pushEnabled: boolean;
  socialAuthEnabled: boolean;
  /** Public web domain (legal pages + support mailbox) for this
   *  environment. Empty until step 11 registers one; the legal URLs and
   *  support email below derive from it so they never drift. */
  webDomain: string;
}

/**
 * Per-environment values.
 *
 * **Both are empty, and that is the correct state until step 11.** Every
 * field follows the file's keyless-safe convention: an empty id makes the
 * feature a documented no-op rather than a misconfiguration. Social sign-in
 * hides its buttons, Amplify omits its `oauth` block, and the app runs
 * against the Floci-emulated pool whose ids arrive through
 * `.floci-outputs.env`.
 *
 * They started out holding the FORK'S REAL PRODUCTION VALUES — a live
 * Cognito pool with real users, a live App Store listing, `getgymprint.com`.
 * That is not untidy, it is dangerous: a production build of this app would
 * have authenticated against another product's user pool. It is the same
 * shape as the checked-in domain presets removed in step 02, which fired on
 * default-stage deploys and pointed the API at the wrong zone.
 *
 * Fill these at step 11, from the outputs of the first real deploy. Until
 * then, an empty value is a deliberate assertion that this app has no
 * production identity yet.
 */
const EMPTY_ENVIRONMENT: Omit<
  EnvironmentConfig,
  "sentryEnvironment" | "webDomain"
> = {
  bundleIds: { ios: "", android: "" },
  revenueCat: { iosKey: "", androidKey: "" },
  cognito: {
    userPoolId: "",
    userPoolClientId: "",
    backendApiUrl: "",
    apiClientKey: "",
    oauthDomain: "",
  },
  admin: { userPoolId: "", userPoolClientId: "", apiUrl: "" },
  appStoreNumericId: "",
  appStoreListingIsLive: false,
  apnsBundleId: "",
  pushEnabled: false,
  socialAuthEnabled: false,
};

const ENVIRONMENTS: Record<AppEnv, EnvironmentConfig> = {
  sandbox: {
    ...EMPTY_ENVIRONMENT,
    sentryEnvironment: "sandbox",
    /**
     * STILL EMPTY, AND THAT IS THE POINT — see `production` below.
     *
     * Every build made today resolves to `sandbox`: nothing in
     * `mobile/ios` sets `APP_ENV`, so `currentAppEnv()` takes its default.
     * Pasting the domain here would therefore turn the Settings legal rows
     * on IMMEDIATELY, in TestFlight, pointing at pages that do not exist
     * yet — the dead-link failure this whole block was written to prevent.
     *
     * Paste `"matcha.smedje.com"` here the day `web/` is actually served
     * from that host, and not before. That single edit turns the About
     * section on everywhere at once, with no code change anywhere else.
     */
    webDomain: "",
  },
  production: {
    ...EMPTY_ENVIRONMENT,
    sentryEnvironment: "production",
    /**
     * The deployed stack, read off its own CloudFormation outputs.
     *
     * `MatchaStack` in account 406903663328, eu-west-1, deployed
     * 2026-09-02. These are not secrets: a user pool id and an app client
     * id are public identifiers that every copy of the app carries, and
     * the client is configured without a secret precisely so it can be.
     *
     * `oauthDomain` is the BRANDED Hosted-UI host, live since the deploy
     * of 2026-09-02 that created the Apple and Google providers. It was
     * deliberately empty before then — empty is what hides the social
     * buttons, and a visible button that fails is worse than an absent
     * one. The long `findmematcha-406903663328.auth.eu-west-1.
     * amazoncognito.com` still resolves and is still registered as a
     * return URL with both IdPs, so a rollback to it needs no portal work.
     *
     * `apiClientKey` likewise: empty means the backend does not install
     * the gate and the client sends no header, so the two agree without
     * either knowing about the other.
     */
    cognito: {
      ...EMPTY_ENVIRONMENT.cognito,
      userPoolId: "eu-west-1_GMGZWDeFs",
      userPoolClientId: "43freevm1h7rrj65ek9o4ro4h0",
      backendApiUrl: "https://api.matcha.smedje.com",
      oauthDomain: "auth.matcha.smedje.com",
    },
    /**
     * Where the public site lives.
     *
     * `matcha.smedje.com` — a subdomain of a domain bought to host many small
     * apps, so matcha is one tenant of it rather than the owner of a name of
     * its own. `api` and `cdn` sit beside it inside the same delegated child
     * zone, which is what lets this project's stack create its own records
     * without write access to the shared parent.
     *
     * NOT `findmematcha.com`. That name belongs to somebody else and serves
     * somebody else's site. It was written all through this repository —
     * canonical URLs on every legal page, a support address on six pages, the
     * list share link, and a proposed `applinks:` entitlement that would have
     * associated this app with a host we do not control.
     *
     * Setting it here rather than in `sandbox` is the safe half of the
     * wiring: `production` is reached only by a build that explicitly sets
     * `APP_ENV=production`, which nothing does today, so this changes nothing
     * about any current build or deploy. It becomes live exactly when
     * somebody activates the App Store environment — the same moment the site
     * has to be up anyway, because App Store Connect will not accept a
     * submission without a reachable privacy policy.
     *
     * BEFORE a production build ships, `web/` must be deployed to this host
     * and `https://matcha.smedje.com/legal/privacy` must answer 200. The host
     * has to map an extensionless path to the directory's `index.html` (an S3
     * website endpoint, Netlify/Vercel/Cloudflare Pages, or a CloudFront
     * viewer-request function all do).
     *
     * It also turns `marketing.supportEmail` into
     * `support@matcha.smedje.com`, which is the address the SES inbound
     * catch-all is designed to receive — but that mailbox has to exist and
     * forward somewhere before the support page is telling the truth.
     */
    webDomain: "matcha.smedje.com",
    /**
     * The App Store record this build belongs to.
     *
     * `6807339084` — App Store Connect → Find Me Matcha → App Information →
     * Apple ID (SKU `findmematcha`). It is what turns the force-upgrade
     * gate's "Update" CTA into a link to THIS listing rather than the
     * App Store's front page, and it is what `legal.appStoreUrl` composes
     * the download badge from. Not a secret: it is in the public URL of
     * every listing.
     */
    appStoreNumericId: "6807339084",

    /** Not yet. The record exists; the page does not. See the field's
     *  own comment — this is release day's one-line change. */
    appStoreListingIsLive: false,

    /**
     * Native identifiers, for the two things that read them: the Sentry
     * `dist`/app-id tag, and the Play-store deep link on the Android
     * upgrade CTA.
     *
     * The two are DELIBERATELY different — iOS registered under the
     * `com.mads.` prefix to sit beside the other App IDs on that developer
     * account, Android under its own name. `mobile/ios/fastlane/Appfile`
     * says the same thing at more length. Do not "fix" one to match.
     */
    bundleIds: {
      ios: "com.mads.findmematcha",
      android: "com.findmematcha",
    },

    /**
     * Apple + Google sign-in are LIVE on this pool.
     *
     * Turned on with the deploy of 2026-09-02, which created the two
     * `UserPoolIdentityProvider` resources and the Hosted-UI domain named
     * in `cognito.oauthDomain` above. The two switches are separate on
     * purpose and both have to be right: `oauthDomain` is what the mobile
     * app needs to open the hosted UI at all (empty ⇒ the buttons are
     * hidden), and this flag is the CDK-side default so a plain
     * `cdk deploy` cannot silently remove the providers it created.
     */
    socialAuthEnabled: true,

    /**
     * `pushEnabled` and `apnsBundleId` stay at their EMPTY defaults, and
     * that is accurate rather than pending.
     *
     * The APNs auth key exists (`Find Me Matcha APNs`, key id 78UA76YZYX,
     * team-scoped, sandbox + production) and the App ID carries the Push
     * Notifications capability — but nothing in this repository sends a
     * push or even collects a device token:
     * `platform/notifications.ts#getRemotePushToken` returns `null` with a
     * comment saying the native registrar was never ported, and there is
     * no SNS platform application in the stack. Flipping `pushEnabled`
     * here would not enable anything; it would only make the config claim
     * a capability the code does not have.
     */

    /**
     * The admin console's own Cognito pool and API.
     *
     * `apiUrl` is set because the host is decided by the stack, not by a
     * deploy: `domainConfig()` composes `admin-api.<zone>` from
     * `MATCHA_DOMAIN_ZONE`, and `infrastructure/scripts/deploy.sh` pins that
     * zone to `matcha.smedje.com`. So the URL is knowable before the first
     * deploy, and writing it here is what lets the console be built against
     * production without anybody pasting a hostname.
     *
     * The two pool ids were EMPTY here until the pool existed — they are
     * CloudFormation outputs, so there was nothing to write. `AdminAuth`
     * was deployed on 4 September 2026 and these are its outputs, read
     * back out of the stack rather than copied from a terminal.
     *
     * Not secrets: a Cognito pool id and its public app-client id are sent
     * to the browser on every sign-in, which is why they can live in a
     * config file that ships to the client at all.
     *
     * If you ever rebuild the pool, these move with it. A stale pair is
     * worse than an empty one — an empty one refuses to configure, a wrong
     * one fails inside the first auth call and reads as "sign-in is
     * broken".
     */
    admin: {
      ...EMPTY_ENVIRONMENT.admin,
      userPoolId: "eu-west-1_wG6E9WBQd",
      userPoolClientId: "2sioafepsluhfkjvhqpb6pv5e9",
      apiUrl: "https://admin-api.matcha.smedje.com",
    },
  },
};

const ENV = currentAppEnv();
const RESOLVED = ENVIRONMENTS[ENV];

export const appConfig = {
  /** The active environment this config was resolved for. */
  env: ENV,

  /** Public product name. Read by the infrastructure too — the SES `From`
   *  display name and the CloudWatch alarm topic both derive from it, so this
   *  is the one place it is spelled. */
  productName: "Find Me Matcha",

  /** Native app identifiers — PER ENVIRONMENT (the sandbox app ships a
   *  `.sandbox` bundle id so it installs alongside production). */
  bundleIds: RESOLVED.bundleIds,

  // ───────────────────────────────────────────────────────────────────────
  // MOBILE — paste-to-activate PUBLIC keys (React Native app)
  // ───────────────────────────────────────────────────────────────────────

  /** RevenueCat public SDK keys — a SEPARATE RevenueCat app per environment
   *  (hard split). Empty ⇒ the safe stub runs and the paywall shows "not
   *  available yet". Public SDK keys are safe to ship in the bundle. */
  revenueCat: {
    /** iOS public SDK key (starts with `appl_`). */
    iosKey: RESOLVED.revenueCat.iosKey,
    /** Android public SDK key (starts with `goog_`). */
    androidKey: RESOLVED.revenueCat.androidKey,
    /** The entitlement identifier that means "paid". One tier. Shared. */
    entitlement: "pro",
  },

  /** PostHog analytics — SHARED project, split by the `env` property above
   *  (soft split). Empty key ⇒ `platform.analytics` stays the DEV-logger /
   *  release no-op. Project API keys are public/publishable (safe to
   *  commit), like the Sentry DSN + RevenueCat public keys. */
  postHog: {
    /** Project API key (`phc_…`). US cloud project (id 524417). */
    apiKey: "phc_CHCiVM9S5mekPfgf9pDNC3vkRvm4n6PkHSivzSrZ8ZAE" as string,
    /** US cloud host — must match the project's region. */
    host: "https://us.i.posthog.com",
  },

  /**
   * Sentry crash + error reporting.
   *
   * TWO projects in the `mads-gd` org (EU region), one per runtime, split
   * further by the `environment` tag below (soft split, sandbox vs
   * production). Keyless-safe: an empty DSN ⇒ the SDK is NOT initialised.
   * DSNs are client-embeddable and are NOT secrets — the write-only public
   * key is the whole point of the format. Mobile reads `dsn` +
   * `environment`; CDK passes `backendDsn` + `environment` into the Lambda,
   * so a change here reaches the backend only on the next `cdk deploy`.
   *
   * BOTH VALUES WERE THE FORK'S UNTIL 2026-09-02, and it was invisible: a
   * DSN is an opaque URL, both projects were in the same org, and nothing
   * anywhere errors when events land in the wrong project. Matcha's crashes
   * and its backend's 500s were being filed against a gym-tracking app —
   * which made matcha's data unfindable and the other product's unusable.
   * If you fork this repo again, these two lines are the ones to change
   * FIRST, before the first build that can phone home.
   */
  sentry: {
    /** Mobile (React Native) project DSN — `matcha-mobile`. */
    dsn: "https://7f03ae5b3c8ba36c88f46d71dfb42089@o4511773673259008.ingest.de.sentry.io/4512018268225616" as string,
    /** Backend (Lambda) project DSN — `matcha-api`. */
    backendDsn:
      "https://a4e81d00ca5ae30149c74ccc022a5e5e@o4511773673259008.ingest.de.sentry.io/4512018272092240" as string,
    /** Performance-trace sample rate. Kept low so the free 5k/mo event quota
     *  is spent on errors, not transactions. The quota is ORG-WIDE on the
     *  Developer plan, shared with every other project in `mads-gd` — so
     *  this number protects the other products too. */
    tracesSampleRate: 0.05,
    /** The `environment` tag applied to every event — `sandbox` or
     *  `production` — so one Sentry project cleanly separates the two. */
    environment: RESOLVED.sentryEnvironment,
  },

  /** Cognito / Amplify config — PER ENVIRONMENT (a separate user pool +
   *  backend stack each). The Hosted-UI OAuth domain is the paste-point that
   *  turns on social sign-in. */
  cognito: {
    userPoolId: RESOLVED.cognito.userPoolId,
    userPoolClientId: RESOLVED.cognito.userPoolClientId,
    backendApiUrl: RESOLVED.cognito.backendApiUrl,
    /**
     * The shared header every API request carries — a speed bump, not a
     * secret and never authentication.
     *
     * It ships inside the bundle, so anybody who unzips an IPA or proxies
     * their own phone has it in a minute. What it removes is the drive-by: a
     * scanner walking `/me`, `/reviews`, `/cafes` against the host gets 403
     * on every path and learns nothing about which exist. That is the whole
     * benefit and it is worth being honest about the size of it.
     *
     * Empty by default, and empty means OFF on both sides — the backend does
     * not install the middleware without `API_CLIENT_KEY`, and the client
     * sends no header. Rotating it is an app release, or an OTA bundle, which
     * is why the OTA route is deliberately NOT behind the gate: the mechanism
     * that fixes a bad key must not need the key.
     */
    apiClientKey: RESOLVED.cognito.apiClientKey,
    /** Hosted-UI domain for federated (Apple / Google) sign-in. Empty ⇒
     *  the social buttons are HIDDEN and Amplify omits the `oauth` block. */
    oauthDomain: RESOLVED.cognito.oauthDomain,
    /** Deep-link callbacks Cognito redirects to after the IdP flow.
     *
     *  These MUST match three things or federated sign-in fails with a
     *  redirect_mismatch the user cannot act on: the UserPoolClient's
     *  callback/logout URLs (`infrastructure/lib/constructs/cognito-auth.ts`),
     *  the scheme registered in the native projects, and the deep-link
     *  prefixes in `mobile/src/shared/lib/navigation/linking.ts`. */
    redirectSignIn: "matcha://callback",
    redirectSignOut: "matcha://signout",
  },

  /** The ADMIN console's Cognito pool + API — a DIFFERENT pool from
   *  `cognito` above so an end-user token can never reach an admin route.
   *  The console is back: `AdminCognitoAuth` and the second `HttpApiBackend`
   *  are in `MatchaStack`, and `apps/admin-backend` serves `openapi-admin/`.
   *  The pool ids here are still empty until that stack has been deployed
   *  and its outputs pasted in; the keyless-safe contract (empty ⇒ refuse to
   *  configure) is what makes the gap between those two moments safe. */
  admin: {
    userPoolId: RESOLVED.admin.userPoolId,
    userPoolClientId: RESOLVED.admin.userPoolClientId,
    apiUrl: RESOLVED.admin.apiUrl,
  },

  /** App Store / Play store product-page targeting for the mobile app. */
  appStore: {
    /** Numeric App Store id (App Store Connect → App Information → Apple ID).
     *  `null` ⇒ the iOS "update" CTA opens the App Store app itself. Per
     *  environment (separate ASC records). */
    numericId: RESOLVED.appStoreNumericId,
    /** False until a version is released — see the field's comment. */
    listingIsLive: RESOLVED.appStoreListingIsLive,
    /**
     * App Store LISTING copy — the SINGLE source of truth for the English
     * store title, subtitle and keyword field. The Fastlane metadata files
     * (`mobile/ios/fastlane/metadata/en-US/{name,subtitle,keywords}.txt`)
     * MUST mirror these verbatim; keep them in sync whenever either changes,
     * and run `node mobile/fastlane/check-metadata.mjs` after.
     *
     * ENGLISH ONLY. The other six languages are not duplicated here — they
     * live only in the Fastlane tree, because a second copy of seven
     * languages is a second thing to keep in step and this file is read by
     * the app, the site and CDK, none of which need them.
     *
     * Apple hard limits: title ≤ 30, subtitle ≤ 30, keywords ≤ 100 — all
     * counted including spaces and, for keywords, including the commas.
     */
    listing: {
      /** Apple app name (App Store Connect → App Information). 14/30. */
      appleTitle: "Find Me Matcha",
      /**
       * Apple subtitle — indexed for search, and the second-strongest field
       * after the name.
       *
       * WAS "Strength Log & Gym Equipment", which is the FORK's subtitle: it
       * described a gym tracker and would have shipped on this app's store
       * page. Replaced 2026-09-02.
       *
       * Carries only words the name does not already index — the name spends
       * "find", "me" and "matcha", so this buys "café", "map", "guide" and
       * "reviews", which are the terms that actually return café-discovery
       * apps in Apple's search rather than restaurant games. 27/30.
       */
      appleSubtitle: "Café map, guide and reviews",
      /**
       * Apple keyword field. Comma-separated, NO space after the comma (a
       * space is a character Apple charges for and does not use), and no
       * word repeated from the title or subtitle above — those two are
       * indexed separately and Apple combines across all three, so a repeat
       * is budget spent twice.
       *
       * Per-term reasoning, and the same for the six other languages, is in
       * `mobile/fastlane/README.md`. 93/100.
       */
      appleKeywords:
        "coffee,tea,near,shop,spots,places,latte,specialty,barista,ceremonial,hopping,laptop,wifi,work",
    },
  },

  /** In-app store-review prompt tuning. Shared across environments. */
  reviewPrompt: {
    /**
     * Days of app experience — measured from the FIRST persisted workout to
     * the current one — required before a review prompt becomes eligible.
     * DEFAULT 3 (LAUNCH); dial back to 7 once a healthy review base exists.
     */
    minExperienceDays: 3,
  },

  /**
   * CLIENT-READABLE app feature flags — baked into the mobile bundle at
   * build time (distinct from `infra.features`, which are deploy-time CDK
   * synth flags). Flipping one of these requires a new app build; that is
   * intentional for "ship dark until we decide" features.
   */
  features: {
    /**
     * Instagram account linking (OAuth round trip → the handle shown on a
     * profile). DEFAULT OFF until step 10 ships the flow and the backend
     * holds an app registration; the Settings row and the connect button are
     * hidden while it is false, so an accidental flip cannot half-enable it.
     *
     * This slot previously held `stravaEnabled`, whose entire backend, SSM
     * parameters and infrastructure were the fork's. Removed with them.
     */
    instagramLinkEnabled: false,
  },

  // ───────────────────────────────────────────────────────────────────────
  // LEGAL — shared by the mobile paywall/consent AND the marketing site
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Canonical legal URLs, or `null` while there is no domain to host them on.
   *
   * **Null rather than a broken URL, and that is the whole point of this
   * block.** These were unconditional template literals, so with `webDomain`
   * empty — which is its correct state until step 11 — they resolved to
   * `https:///legal/terms`. Every screen that linked them was shipping a
   * dead link that looks live until somebody taps it, which is worse than
   * not offering the link at all: a Privacy Policy that 404s is a broken
   * promise, where an absent one is a gap you can see.
   *
   * The consequence is the keyless-safe contract this file is built on,
   * applied to legal: a screen renders the links when they are configured
   * and renders nothing when they are not, so the day a domain is pasted
   * into `webDomain` above they appear everywhere at once with no code
   * change and no chance of a dead link shipping in between.
   *
   * Apple Guideline 3.1.2 requires an auto-renewable-subscription paywall to
   * link BOTH from inside the app, and App Store submission requires a
   * privacy policy URL in App Store Connect regardless — so this staying
   * null is a launch blocker, not a permanent state.
   */
  legal: {
    termsUrl: RESOLVED.webDomain
      ? `https://${RESOLVED.webDomain}/legal/terms`
      : (null as string | null),
    privacyUrl: RESOLVED.webDomain
      ? `https://${RESOLVED.webDomain}/legal/privacy`
      : (null as string | null),
    /** Apple's standard EULA — the accepted fallback if our Terms are ever
     *  unavailable. A real, permanent URL on Apple's own domain, so it is
     *  never null. Reference only; nothing links it today. */
    appleStandardEulaUrl:
      "https://www.apple.com/legal/internet-services/itunes/dev/stdeula/",
  },

  // ───────────────────────────────────────────────────────────────────────
  // COMPLIANCE — age gate + data-retention defaults
  // ───────────────────────────────────────────────────────────────────────

  compliance: {
    /**
     * The minimum age, in whole years — and the ONLY definition of it.
     *
     * This said 16, described itself as informational, and explained that
     * Apple compliance "for a standard fitness app" is the age-rating
     * questionnaire rather than an in-app gate. All three were inherited from
     * the fork and all three are wrong here: this app HAS an in-app
     * date-of-birth gate, it enforces 13, and nothing read this number. Two
     * numbers, one of them fiction, is worse than either — a reviewer reading
     * the config and a reviewer using the app were told different things.
     *
     * 13 is the floor the App Store expects for an app carrying
     * user-generated content, and it is Denmark's digital-consent age under
     * GDPR Article 8.
     *
     * **Article 8 lets member states set anything from 13 to 16, and a
     * worldwide release makes that live.** Germany, the Netherlands and
     * Ireland are 16; France 15; Spain and Italy 14; Denmark and Portugal 13.
     * A single global 13 holds only while the lawful basis for the app's core
     * processing is CONTRACT rather than consent — Article 8 governs
     * consent-based information-society services. If anything moves to a
     * consent basis this becomes per-market, and this is the one place to
     * change it.
     */
    minAgeYears: 13,
  },

  // ───────────────────────────────────────────────────────────────────────
  // MARKETING — legal-entity, support, store links, display pricing
  // ───────────────────────────────────────────────────────────────────────

  marketing: {
    /** Support mailbox shown on /support (Apple's support-URL target) and
     *  the contact for privacy/GDPR requests. TODO: create + verify. */
    supportEmail: `support@${RESOLVED.webDomain}`,
    /** Marketing-site root (no trailing slash), env-resolved from
     *  `webDomain` — the one link that is always valid to hand out (a shared
     *  café, a profile). Empty until step 11 registers a domain. */
    webUrl: RESOLVED.webDomain ? `https://${RESOLVED.webDomain}` : "",
    /** Data controller that publishes the app. Run by a private individual
     *  (not a company), so this is the person's name. */
    companyLegalName: "Mads Petersen",
    /** Controller contact locality for the privacy policy. GDPR needs contact
     *  details, not a home address — the support email (above) is the reachable
     *  contact, so this stays city + country only (no street), which keeps a
     *  private individual's home address off a public page. */
    companyAddress: "Copenhagen, Denmark",
    /** Governing law / venue. Confirmed by owner (2026-08-03). */
    governingLaw: "Denmark",
    /** App Store product-page URL for the download badge. Region-agnostic
     *  (`/app/id…`) so it redirects to each visitor's storefront. Empty until
     *  there is a listing — it held the FORK's live App Store id, which would
     *  have sent anyone tapping "Download" to a gym-tracking app. */
    appStoreUrl: RESOLVED.appStoreNumericId
      ? `https://apps.apple.com/app/id${RESOLVED.appStoreNumericId}`
      : "",
    /** Play listing URL — Android ships later. `null` until it exists. */
    googlePlayUrl: null as string | null,
    /**
     * Display-only prices, or `null` where nothing is sold.
     *
     * ALL THREE ARE NULL, and that is the accurate state. `$4.99` / `$29.99`
     * were the FORK's subscription prices and they survived into this file;
     * the App Store listing written on 2026-09-02 says in seven languages
     * that there is no subscription and nothing to unlock, which is true —
     * there is no paywall screen in `mobile/src`, no RevenueCat key
     * configured in either environment, and the store submission declares no
     * in-app purchases. A price here is a promise the product does not make.
     *
     * If a paid tier is ever added, these come back AND the listing copy,
     * the age-rating `advertising` answer and Apple's Guideline 3.1.2
     * obligations all change together.
     */
    priceMonthly: null as string | null,
    priceAnnual: null as string | null,
    /** Optional one-time lifetime price. `null` ⇒ not offered / not shown. */
    priceLifetime: null as string | null,
    /** "Last updated" markers on the legal pages — bump when text changes. */
    privacyLastUpdated: "3 August 2026",
    termsLastUpdated: "17 July 2026",
  },

  // ───────────────────────────────────────────────────────────────────────
  // INFRA — public identifiers, feature-flag defaults, SSM param NAMES
  // ───────────────────────────────────────────────────────────────────────

  infra: {
    /** Deploy-time feature defaults, resolved PER ENVIRONMENT (production
     *  ON, sandbox OFF) so a plain `cdk deploy` for prod keeps social auth
     *  + push enabled — a bare deploy can no longer tear them down. An env
     *  var (`PUSH_ENABLED=1` / `SOCIAL_AUTH_ENABLED=1`) still force-enables
     *  on top (used to opt sandbox in). */
    features: {
      /** Server-sent push (SNS platform apps for APNs). */
      pushEnabled: RESOLVED.pushEnabled,
      /** Federated sign-in (Apple / Google via Cognito). */
      socialAuthEnabled: RESOLVED.socialAuthEnabled,
    },

    /** APNs PUBLIC identifiers (safe to commit). The private `.p8` key is a
     *  SECRET → `ssmParams.apnsPrivateKey`. `bundleId` is per environment. */
    apns: {
      teamId: "L4B6XDBK53",
      bundleId: RESOLVED.apnsBundleId,
    },

    /** Force-upgrade floor the mobile app's launch-time gate compares
     *  against (`GET /minimum-app-version`, `useMinimumVersionGate`). `'0.0.0'`
     *  is a genuine no-op — every real semver compares greater, so nobody is
     *  walled out. Raise ONLY the specific platform's floor when a version is
     *  confirmed bad and its next release already ships the wall itself;
     *  never guess a floor without checking install-base analytics first. */
    minAppVersion: {
      ios: "0.0.0",
      android: "0.0.0",
    },

    /** SSM parameter NAMES. With a SEPARATE AWS account per environment these
     *  names are identical in each account (no per-env namespacing needed) —
     *  each account holds its own values. Values are never committed. */
    ssmParams: {
      /** Postgres (Neon) connection string — SecureString. Written
       *  out-of-band (`aws ssm put-parameter --type SecureString`), never by
       *  CDK, so the value never enters the template. Every DB-backed Lambda
       *  receives only this NAME and decrypts it at runtime. */
      databaseUrl: "/findmematcha/database/url",
      /** Google OAuth client id — String. */
      googleClientId: "/findmematcha/google/client_id",
      /** Google OAuth client secret — SecureString. */
      googleClientSecret: "/findmematcha/google/client_secret",
      /** Apple Sign in Services ID — String. */
      appleServicesId: "/findmematcha/apple/services_id",
      /** Apple developer Team ID — String. */
      appleTeamId: "/findmematcha/apple/team_id",
      /** Apple Sign in Key ID — String. */
      appleKeyId: "/findmematcha/apple/key_id",
      /** Apple `.p8` private key contents — SecureString. */
      applePrivateKey: "/findmematcha/apple/private_key",
      /** CloudFront signing private key (progress photos) — SecureString. */
      photosCloudfrontPrivateKey: "/findmematcha/photos/cloudfront-private-key",
      /** CloudFront signing public key (progress photos) — String. */
      photosCloudfrontPublicKey: "/findmematcha/photos/cloudfront-public-key",
    },
  },
} as const;

export type AppConfig = typeof appConfig;
